/**
 * SupportTicketsBlock — Church owner side
 * Submit a ticket; Premium gets PRIORITY badge automatically.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Send, MessageSquare, Crown } from "lucide-react";

const inp = "w-full bg-black border border-white/15 focus:border-[#D4AF37] outline-none px-3 py-2.5 rounded text-sm text-white";

export default function SupportTicketsBlock({ church }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ subject: "", message: "", category: "general" });
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/church-builder/churches/${church.id}/tickets`);
      setTickets(data);
    } catch { /* */ }
    finally { setLoading(false); }
  };
  useEffect(() => { if (church?.id) load(); /* eslint-disable-next-line */ }, [church?.id]);

  const submit = async () => {
    if (!form.subject.trim() || !form.message.trim()) return toast.error("Subject + message required");
    setSending(true);
    try {
      await api.post(`/church-builder/churches/${church.id}/tickets`, form);
      toast.success("Ticket submitted");
      setForm({ subject: "", message: "", category: "general" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  const isPremium = church?.subscription_plan === "premium";

  return (
    <div className="space-y-6">
      <div data-testid="cb-tickets-form" className="luxury-card rounded-xl p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="font-serif-display text-2xl">Contact Support</div>
          {isPremium && <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] tracking-widest uppercase font-bold border border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#D4AF37]"><Crown size={11} /> Priority</span>}
        </div>
        <div className="text-xs text-neutral-400 mt-1">{isPremium ? "Your messages reach Hanson at the top of the queue." : "Standard response within 1–2 business days."}</div>

        <div className="mt-5 grid sm:grid-cols-2 gap-3">
          <input data-testid="cb-ticket-subject" className={inp} placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
          <select className={inp} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            <option value="general">General</option>
            <option value="billing">Billing</option>
            <option value="bug">Bug</option>
            <option value="feature">Feature request</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>
        <textarea data-testid="cb-ticket-message" className={`${inp} mt-3`} rows={5} placeholder="How can we help?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
        <div className="mt-4">
          <button data-testid="cb-ticket-send" disabled={sending} onClick={submit} className="btn-gold px-5 py-2 rounded text-[10px] tracking-widest uppercase font-semibold inline-flex items-center gap-2">
            {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send ticket
          </button>
        </div>
      </div>

      <div data-testid="cb-tickets-list" className="luxury-card rounded-xl p-6">
        <div className="font-serif-display text-xl flex items-center gap-2"><MessageSquare size={16} className="text-[#D4AF37]" /> Your tickets</div>
        <div className="mt-4 space-y-3">
          {loading ? <Loader2 className="animate-spin text-[#D4AF37]" size={16} /> :
            tickets.length === 0 ? <div className="text-xs text-neutral-500">No tickets yet.</div> :
            tickets.map((t) => (
              <div key={t.id} className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-sm text-white">{t.subject}</div>
                    <div className="text-[11px] text-neutral-500 mt-0.5">{t.category} · {new Date(t.created_at).toLocaleString()}</div>
                  </div>
                  <span className={`text-[10px] tracking-widest uppercase px-2 py-0.5 rounded border ${
                    t.status === "open" ? "border-amber-500/40 text-amber-300" :
                    t.status === "in_progress" ? "border-blue-500/40 text-blue-300" :
                    t.status === "resolved" ? "border-emerald-500/40 text-emerald-300" :
                    "border-neutral-500/40 text-neutral-300"
                  }`}>{t.status}</span>
                </div>
                <p className="text-neutral-300 text-sm mt-3 whitespace-pre-wrap">{t.message}</p>
                {t.admin_reply && (
                  <div className="mt-3 rounded border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-3">
                    <div className="text-[10px] tracking-widest uppercase text-[#D4AF37]">HOH Reply</div>
                    <p className="text-neutral-200 text-sm mt-1 whitespace-pre-wrap">{t.admin_reply}</p>
                  </div>
                )}
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );
}
